import React, { useEffect, useState } from "react";
import { PanelsTopLeft, ArrowRightToLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useRequest from "../../../../components/hook/use-req";
import { UserResponse } from "../../../../components/types";
import separationImage from "../../../../assets/images/Central AI Core.png";
import { OVERVIEW_DESTINATION_ROWS, OVERVIEW_SOURCE_ROWS } from "./overview-data";

// Shared row/header sizing so the SVG connector lines below line up exactly
// with the Sources/Destinations list rows regardless of viewport width.
const ROW_H = 48;
const HEADER_H = 53;

const ConnectorList: React.FC<{
  title: string;
  icon: React.ReactNode;
  rows: typeof OVERVIEW_SOURCE_ROWS;
  addLabel: string;
  onAdd: () => void;
}> = ({ title, icon, rows, addLabel, onAdd }) => (
  <div className="flex-1 min-w-0 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
    <div
      className="flex items-center justify-between px-4 border-b border-gray-100 dark:border-gray-800"
      style={{ height: HEADER_H }}
    >
      <div className="flex items-center gap-2 font-semibold text-sm text-gray-900 dark:text-gray-100">
        {icon}
        {title}
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
      >
        {addLabel}
      </button>
    </div>
    <div>
      {rows.map((row) => (
        <div
          key={row.id}
          className="flex items-center gap-2.5 px-4 border-b border-gray-50 dark:border-gray-800/60 last:border-b-0"
          style={{ height: ROW_H }}
        >
          {row.icon}
          <span className="text-sm text-gray-700 dark:text-gray-300">{row.label}</span>
        </div>
      ))}
    </div>
  </div>
);

// The animated Sources -> hub -> Destinations connector panel on the
// Connections Overview screen. Lines fan from each row into the center hub
// icon and back out again, each carrying a small traveling highlight; the
// hub itself breathes (scales up/down) continuously via hub-pulse below.
export const ConnectionFlow: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const rowCount = Math.max(OVERVIEW_SOURCE_ROWS.length, OVERVIEW_DESTINATION_ROWS.length);
  const listHeight = rowCount * ROW_H;
  const svgHeight = HEADER_H + listHeight;
  const hubY = HEADER_H + listHeight / 2;
  const rowY = (i: number) => HEADER_H + i * ROW_H + ROW_H / 2;

  // Hub icon — the org's own logo (same /auth/get_userdata -> org_logo the
  // sidebar/navbar already use), falling back to the static illustration
  // only until that request resolves (or if the org has no logo set).
  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });
  const [user, setUser] = useState<UserResponse | null>(null);
  useEffect(() => {
    (async () => {
      const [response] = await getUserData();
      setUser(response);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hubIcon = user?.payload?.org_logo || separationImage;

  return (
    <div className="flex items-stretch gap-0">
      <style>{`
        @keyframes hub-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes hub-glow {
          0% { transform: translate(-50%, -50%) scale(0.9); opacity: 0.35; }
          70%, 100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
        }
        .hub-pulse { animation: hub-pulse 2.4s ease-in-out infinite; }
        .hub-glow { animation: hub-glow 2.4s ease-out infinite; }
      `}</style>

      <ConnectorList
        title="Sources"
        icon={<PanelsTopLeft size={16} className="text-gray-500 dark:text-gray-400" />}
        rows={OVERVIEW_SOURCE_ROWS}
        addLabel="Add New Source"
        onAdd={() => navigate("/cdp/connections/sources")}
      />

      {/* Connector lines + hub — hidden on narrow screens where the two
          list cards stack instead of sitting side by side. */}
      <div className="relative hidden sm:block w-28 md:w-40 lg:w-56 shrink-0" style={{ height: svgHeight }}>
        <svg
          width="100%"
          height={svgHeight}
          viewBox={`0 0 200 ${svgHeight}`}
          preserveAspectRatio="none"
          className="absolute inset-0"
        >
          <defs>
            <linearGradient id="connFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.65" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.35" />
            </linearGradient>
          </defs>

          {OVERVIEW_SOURCE_ROWS.map((row, i) => {
            const y = rowY(i);
            const id = `src-path-${row.id}`;
            return (
              <g key={row.id}>
                <path
                  id={id}
                  d={`M 0 ${y} C 70 ${y}, 70 ${hubY}, 100 ${hubY}`}
                  stroke="url(#connFlowGradient)"
                  strokeWidth="1.5"
                  fill="none"
                  className="opacity-70"
                />
                <circle r="2.5" fill="#4f46e5" className="opacity-80">
                  <animateMotion dur="3s" repeatCount="indefinite" begin={`${i * 0.25}s`}>
                    <mpath href={`#${id}`} />
                  </animateMotion>
                </circle>
              </g>
            );
          })}

          {OVERVIEW_DESTINATION_ROWS.map((row, j) => {
            const y = rowY(j);
            const id = `dst-path-${row.id}`;
            return (
              <g key={row.id}>
                <path
                  id={id}
                  d={`M 100 ${hubY} C 130 ${hubY}, 130 ${y}, 200 ${y}`}
                  stroke="url(#connFlowGradient)"
                  strokeWidth="1.5"
                  fill="none"
                  className="opacity-70"
                />
                <circle r="2.5" fill="#4f46e5" className="opacity-80">
                  <animateMotion dur="3s" repeatCount="indefinite" begin={`${1.5 + j * 0.25}s`}>
                    <mpath href={`#${id}`} />
                  </animateMotion>
                </circle>
              </g>
            );
          })}
        </svg>

        {/* Hub icon — absolutely centered over the SVG's convergence point. */}
        <div
          className="absolute left-1/2"
          style={{ top: hubY }}
        >
          <div className="hub-glow absolute left-0 top-0 w-[120px] h-[120px] rounded-full bg-[#0030C3] dark:bg-indigo-500/30" />
          <div className="hub-pulse absolute left-0 top-0 w-[120px] h-[120px] rounded-full bg-[#3497F9] shadow-lg flex items-center justify-center">
            <img src={hubIcon} alt="" className="object-contain rounded-full" />
          </div>
        </div>
      </div>

      <ConnectorList
        title="Destinations"
        icon={<ArrowRightToLine size={16} className="text-gray-500 dark:text-gray-400" />}
        rows={OVERVIEW_DESTINATION_ROWS}
        addLabel="Add New Destination"
        onAdd={() => navigate("/cdp/connections/destinations")}
      />
    </div>
  );
};

export default ConnectionFlow;
